import { nextStage } from "@atzmai/schema";
import { useState } from "react";
import { api, errorText, fmtBytes } from "../../lib/api.ts";
import { useApi } from "../../lib/useApi.ts";
import { Loading, ScreenHead, Table, useStaff } from "./ConsoleLayout.tsx";

interface Release {
  version: string; channel: string; size_bytes: number; rollout_pct: number; status: string;
  baseline_fail: number; attempts: number; failures: number; stage_at: string | null;
}

const STATUS: Record<string, string> = { rolling: "bg-warn/15 text-warn", paused: "bg-bad/15 text-bad", live: "bg-good/15 text-good", retired: "bg-glass text-ink-3", draft: "bg-glass text-ink-3" };

export function Releases() {
  const { canOperate } = useStaff();
  const { data, error, reload } = useApi<{ releases: Release[] }>("/api/console/releases");
  const [msg, setMsg] = useState<string | null>(null);
  // Confirmation lives in the page: the first click arms, the second acts.
  const [armed, setArmed] = useState<string | null>(null);

  async function act(version: string, action: "promote" | "pause" | "resume", extra: object = {}) {
    const key = `${version}:${action}`;
    if (action !== "pause" && armed !== key) { setArmed(key); return; }
    setArmed(null);
    try {
      const r = await api<{ rollout_pct: number; status: string }>(`/api/console/releases/${version}/${action}`, { json: extra });
      setMsg(`${version}: ${action === "promote" ? `promoted to ${r.rollout_pct}%` : action === "pause" ? "paused" : "resumed"}.`);
    } catch (e) {
      setMsg(errorText(e));
    }
    await reload();
  }

  if (!data) return <><ScreenHead label="Releases" /><Loading error={error} /></>;
  return (
    <div className="flex flex-col gap-4">
      <ScreenHead label="Releases" />
      {msg && <p role="status" className="rounded-xl bg-glass px-4 py-2.5 text-[15px] text-ink">{msg}</p>}
      <Table>
        <thead><tr><th>Version</th><th>Channel</th><th>Size</th><th>Rollout</th><th>Apply failure</th><th>Status</th><th /></tr></thead>
        <tbody>
          {data.releases.map((r) => {
            const fail = r.attempts ? (r.failures / r.attempts) * 100 : null;
            const over = fail !== null && fail > r.baseline_fail * 2;
            const next = nextStage(r.rollout_pct);
            return (
              <tr key={r.version}>
                <td className="font-mono">{r.version}</td>
                <td>{r.channel}</td>
                <td>{r.size_bytes ? fmtBytes(r.size_bytes) : "–"}</td>
                <td>
                  <div className="flex items-center gap-2"><div className="h-1.5 w-24 rounded bg-glass"><div className="h-full rounded bg-ring" style={{ width: `${r.rollout_pct}%` }} /></div>{r.rollout_pct}%</div>
                </td>
                <td className={over ? "font-medium text-bad" : ""}>{fail === null ? "–" : `${fail.toFixed(1)}%`} <span className="text-[13px] text-ink-3">of {r.attempts}, base {r.baseline_fail}%</span></td>
                <td><span className={`pill ${STATUS[r.status] ?? ""}`}>{r.status}</span></td>
                <td className="whitespace-nowrap text-right">
                  {canOperate && r.status === "rolling" && <button className="btn btn-danger" onClick={() => void act(r.version, "pause")}>Pause</button>}
                  {canOperate && r.status === "paused" && <button className="btn" onClick={() => void act(r.version, "resume")}>{armed === `${r.version}:resume` ? "Confirm resume" : "Resume"}</button>}
                  {canOperate && r.status === "rolling" && next && (
                    <button className="btn btn-primary ml-2" onClick={() => void act(r.version, "promote")}>
                      {armed === `${r.version}:promote` ? `Confirm ${next}%` : `Promote to ${next}%`}
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
      <p className="text-[13px] text-ink-3">Pause takes effect on the next manifest fetch (within 60 s of edge cache). Canaries hold 48 hours at 1% before promotion. Releases are registered with POST /api/console/releases after the signed packages are on R2.</p>
    </div>
  );
}
